import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import DashboardAppsAPI from '../../api/dashboardApps';

// NOTICE: KAPTION-INTEGRATION
export const state = {
  records: [],
  tokens: {}, // { dashboardAppId: token }
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isDeleting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getRecords(_state) {
    return _state.records;
  },
  // NOTICE: KAPTION-INTEGRATION
  getToken(_state) {
    return dashboardAppId => _state.tokens[dashboardAppId];
  },
};

export const actions = {
  get: async function getDashboardApps({ commit, state: currentState }) {
    // NOTICE: KAPTION-INTEGRATION - Skip fetch if already loaded (avoids redundant API calls
    // from multiple components mounting, e.g. sidebar + fullscreen app view)
    if (currentState?.records?.length > 0) return;
    commit(types.SET_DASHBOARD_APPS_UI_FLAG, { isFetching: true });
    try {
      const response = await DashboardAppsAPI.get();
      commit(types.SET_DASHBOARD_APPS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_DASHBOARD_APPS_UI_FLAG, { isFetching: false });
    }
  },
  create: async function createApp({ commit }, appObj) {
    commit(types.SET_DASHBOARD_APPS_UI_FLAG, { isCreating: true });
    try {
      const response = await DashboardAppsAPI.create(appObj);
      commit(types.CREATE_DASHBOARD_APP, response.data);
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_DASHBOARD_APPS_UI_FLAG, { isCreating: false });
    }
  },
  update: async function updateApp({ commit }, { id, ...updateObj }) {
    commit(types.SET_DASHBOARD_APPS_UI_FLAG, { isUpdating: true });
    try {
      const response = await DashboardAppsAPI.update(id, updateObj);
      commit(types.EDIT_DASHBOARD_APP, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DASHBOARD_APPS_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async function deleteApp({ commit }, id) {
    commit(types.SET_DASHBOARD_APPS_UI_FLAG, { isDeleting: true });
    try {
      await DashboardAppsAPI.delete(id);
      commit(types.DELETE_DASHBOARD_APP, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DASHBOARD_APPS_UI_FLAG, { isDeleting: false });
    }
  },
  // NOTICE: KAPTION-INTEGRATION
  fetchToken: async function fetchToken({ commit }, dashboardAppId) {
    try {
      const response = await DashboardAppsAPI.fetchToken(dashboardAppId);
      commit(types.SET_DASHBOARD_APP_TOKEN, {
        id: dashboardAppId,
        token: response.data.token,
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error?.response?.data?.message || 'Failed to fetch token'
      );
    }
  },
  // NOTICE: KAPTION-INTEGRATION - Per-app secret support
  regenerateSecret: async function regenerateSecret(_, dashboardAppId) {
    try {
      const response = await DashboardAppsAPI.regenerateSecret(dashboardAppId);
      return response.data;
    } catch (error) {
      throw new Error(
        error?.response?.data?.message || 'Failed to regenerate secret'
      );
    }
  },
  // NOTICE: KAPTION-INTEGRATION - Per-app secret support
  fetchSecret: async function fetchSecret(_, dashboardAppId) {
    try {
      const response = await DashboardAppsAPI.fetchSecret(dashboardAppId);
      return response.data;
    } catch (error) {
      throw new Error(
        error?.response?.data?.message || 'Failed to fetch secret'
      );
    }
  },
};

export const mutations = {
  [types.SET_DASHBOARD_APPS_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_DASHBOARD_APPS]: MutationHelpers.set,
  [types.CREATE_DASHBOARD_APP]: MutationHelpers.create,
  [types.EDIT_DASHBOARD_APP]: MutationHelpers.update,
  [types.DELETE_DASHBOARD_APP]: MutationHelpers.destroy,
  // NOTICE: KAPTION-INTEGRATION
  [types.SET_DASHBOARD_APP_TOKEN](_state, { id, token }) {
    _state.tokens = { ..._state.tokens, [id]: token };
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
